// src/services/dataAdapters/index.js
// Factory for DataAdapter instances.
//
// Usage:
//   import { createAdapter } from './dataAdapters';
//   const adapter = createAdapter(dataSource);   // dataSource from DataSourceService
//
// Supported types today: 'mock', 'supabase', 'csv'
// Future types (each will be a separate INFRA story): postgres, mssql, rest

import MockDataAdapter     from './MockDataAdapter';
import SupabaseDataAdapter from './SupabaseDataAdapter';
import CSVDataAdapter      from './CSVDataAdapter';

/**
 * Creates and returns the correct DataAdapter for a given DataSource record.
 *
 * @param {{ type: string, config?: object }} dataSource
 *   The DataSource object as stored by DataSourceService (id, name, type, config, …).
 *
 * @returns {DataAdapter}
 * @throws {Error} when dataSource.type is missing or has no implemented adapter yet
 */
export function createAdapter(dataSource) {
  if (!dataSource || !dataSource.type) {
    throw new Error('createAdapter: dataSource.type is required');
  }

  const config = dataSource.config || {};

  switch (dataSource.type) {
    case 'mock':
      return new MockDataAdapter(config);

    case 'supabase':                          // INFRA-062
      return new SupabaseDataAdapter(config);

    case 'csv':                               // INFRA-065
      return new CSVDataAdapter(config);

    // Stubs for future INFRA stories — uncomment as adapters are implemented:
    // case 'postgres': return new PostgresDataAdapter(config);  // INFRA-063
    // case 'mssql':    return new MssqlDataAdapter(config);     // INFRA-064
    // case 'rest':     return new RestDataAdapter(config);      // INFRA-066

    default:
      throw new Error(
        `createAdapter: no adapter implemented for type "${dataSource.type}"`
      );
  }
}

// Re-export base class and implementations for consumers that need type-checks
export { default as DataAdapter }         from './DataAdapter';
export { default as MockDataAdapter }     from './MockDataAdapter';
export { default as SupabaseDataAdapter } from './SupabaseDataAdapter';
export { default as CSVDataAdapter }      from './CSVDataAdapter';
